/**
 * downloadCsv — shared CSV export helper (US-17-GR-29)
 *
 * Used by the Jobs, Cost, and Billing admin pages to build a CSV from
 * column definitions + rows, and trigger a browser download.
 *
 * Conventions:
 *   - Numbers are written raw/unformatted (no thousands separators, no
 *     currency symbols) — callers pass the underlying numeric value.
 *   - Timestamps are written as ISO-8601 strings — callers pass the raw
 *     ISO string, not a locale-formatted one.
 *   - Filenames follow `{report}-{window|range}-{YYYY-MM-DD}.csv`, where
 *     the trailing date is the export date (not the data window).
 */

export interface CsvColumn<T> {
  /** Column header cell. */
  header: string;
  /** Extracts the raw cell value for a row; null/undefined render as empty. */
  accessor: (row: T) => string | number | null | undefined;
}

/**
 * Characters that trigger formula evaluation in Excel/Sheets/LibreOffice
 * when they lead a CSV field (OWASP CSV Injection mitigation).
 */
const FORMULA_TRIGGER_CHARS = ['=', '+', '-', '@', '\t', '\r'];

/**
 * Escapes a single CSV field per RFC 4180 (quotes, commas, newlines).
 *
 * `isNumeric` marks fields whose underlying accessor value was a `number`
 * (not a string) — those are never formula-injection-prefixed, since a
 * negative numeric value (e.g. `-12.5`) is a safe number, not attacker
 * -controlled text starting with `-`. Only string cells get the leading
 * single-quote guard.
 */
function escapeCsvField(raw: string, isNumeric = false): string {
  let field = raw;
  if (!isNumeric && field.length > 0 && FORMULA_TRIGGER_CHARS.includes(field[0])) {
    field = `'${field}`;
  }
  if (/[",\r\n]/.test(field)) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

function cellToString(value: string | number | null | undefined): { text: string; isNumeric: boolean } {
  if (value === null || value === undefined) return { text: '', isNumeric: false };
  return { text: String(value), isNumeric: typeof value === 'number' };
}

/** Builds a CSV string (CRLF line endings) from column defs + rows. */
export function buildCsv<T>(columns: CsvColumn<T>[], rows: T[]): string {
  const headerLine = columns.map((c) => escapeCsvField(c.header)).join(',');
  const dataLines = rows.map((row) =>
    columns
      .map((c) => {
        const { text, isNumeric } = cellToString(c.accessor(row));
        return escapeCsvField(text, isNumeric);
      })
      .join(','),
  );
  return [headerLine, ...dataLines].join('\r\n');
}

/** Builds the standard `{report}-{window|range}-{YYYY-MM-DD}.csv` filename. */
export function buildCsvFilename(report: string, windowOrRange: string, now: Date = new Date()): string {
  const dateStr = now.toISOString().slice(0, 10);
  return `${report}-${windowOrRange}-${dateStr}.csv`;
}

/**
 * How long an object URL stays alive after the anchor click, before it is
 * revoked. Revoking synchronously after `click()` races the browser: the
 * download is only queued by the click, and Chromium cancels it outright if
 * the blob is gone before it reads it. The delay is FileSaver.js's, for the
 * same reason.
 */
const OBJECT_URL_LIFETIME_MS = 40_000;

/** Triggers a browser download of a pre-built CSV/Blob under the given filename. */
export function triggerCsvBlobDownload(filename: string, content: Blob | string): void {
  const blob =
    content instanceof Blob ? content : new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_LIFETIME_MS);
}

/**
 * Builds a CSV from column defs + rows and triggers a browser download.
 * Empty guard: no-ops (returns false) when `rows` is empty — callers should
 * also disable the triggering button, this is a defensive backstop.
 */
export function downloadCsv<T>(
  report: string,
  windowOrRange: string,
  columns: CsvColumn<T>[],
  rows: T[],
): boolean {
  if (rows.length === 0) return false;
  const csv = buildCsv(columns, rows);
  triggerCsvBlobDownload(buildCsvFilename(report, windowOrRange), csv);
  return true;
}
